import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, KeyRound } from "lucide-react";
import { api } from "../../services/api";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
    Form,
    FormField,
    FormItem,
    FormControl,
    FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";

interface ResetPasswordFormValues {
    email: string;
    code: string;
    newPassword: string;
    confirmNewPassword: string;
}

export const ResetPassword = () => {
    const { t } = useTranslation("auth");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const form = useForm<ResetPasswordFormValues>({
        defaultValues: {
            email: searchParams.get("email") ?? "",
            code: searchParams.get("code") ?? "",
            newPassword: "",
            confirmNewPassword: "",
        },
    });

    const onSubmit = (data: ResetPasswordFormValues) => {
        if (data.newPassword !== data.confirmNewPassword) {
            toast.error(t("resetPassword.mismatchError"));
            return;
        }

        api.put("/forgot-password", data, {
            hideSuccessToast: true,
            hideErrorToast: true,
        })
            .then(() => {
                toast.success(t("resetPassword.success"));
                navigate("/signin");
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    toast.error(t("resetPassword.emailNotFound"));
                } else if (error.response?.status === 422) {
                    const body = error.response.data?.body;
                    const errors = Array.isArray(body)
                        ? body.join(", ")
                        : undefined;
                    toast.error(
                        errors
                            ? t(
                                  "common:api.invalidData",
                                  "Dados inválidos: {{errors}}",
                                  { errors },
                              )
                            : t(
                                  "common:api.validationError",
                                  "Erro de validação nos dados informados.",
                              ),
                    );
                } else if (!error.response) {
                    toast.error(
                        t(
                            "common:api.connectionError",
                            "Erro de conexão ou tempo de limite excedido. Verifique sua internet.",
                        ),
                    );
                } else {
                    toast.error(
                        t(
                            "common:api.unexpected",
                            "Ocorreu um erro inesperado ({{status}})",
                            { status: error.response.status },
                        ),
                    );
                }
            });
    };

    return (
        <div className="bg-background min-h-screen flex flex-col justify-center">
            <div className="flex flex-1" />
            <div className="flex flex-1 justify-center items-center w-full">
                <div className="w-full max-w-sm px-4">
                    <div className="border rounded-lg shadow-sm p-6 bg-card w-full">
                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                className="space-y-6"
                            >
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <img
                                        src={require("../../assets/dream-book-logo.png")}
                                        alt="Logo"
                                        className="max-w-[240px]"
                                    />
                                    <Separator />
                                    <h5 className="text-muted-foreground text-sm text-center">
                                        {t("resetPassword.subtitle")}
                                    </h5>
                                </div>

                                <FormField
                                    control={form.control}
                                    name="email"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                                    <Input
                                                        className="pl-10 h-10"
                                                        placeholder={t(
                                                            "forgotPassword.emailPlaceholder",
                                                        )}
                                                        {...field}
                                                    />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="code"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className="relative">
                                                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                                    <Input
                                                        className="pl-10 h-10 uppercase"
                                                        placeholder={t(
                                                            "resetPassword.codePlaceholder",
                                                        )}
                                                        {...field}
                                                    />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="newPassword"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className="relative">
                                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                                    <Input
                                                        type="password"
                                                        className="pl-10 h-10"
                                                        placeholder={t(
                                                            "resetPassword.passwordPlaceholder",
                                                        )}
                                                        {...field}
                                                    />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="confirmNewPassword"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <div className="relative">
                                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                                    <Input
                                                        type="password"
                                                        className="pl-10 h-10"
                                                        placeholder={t(
                                                            "resetPassword.passwordConfirmationPlaceholder",
                                                        )}
                                                        {...field}
                                                    />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="flex flex-col space-y-2 mt-4">
                                    <Button type="submit" className="w-full">
                                        {t("resetPassword.submitButton")}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="w-full text-primary"
                                        onClick={() => navigate("/signin")}
                                    >
                                        {t("resetPassword.backToLogin")}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </div>
                </div>
            </div>
            <div className="flex flex-[2]" />
        </div>
    );
};
